export const ZONE = "Pacific/Auckland";

export function formatWhen(iso: string, options: Intl.DateTimeFormatOptions = {}): string {
  return new Intl.DateTimeFormat("en-NZ", {
    timeZone: ZONE,
    ...options,
  }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return formatWhen(iso, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatTime(iso: string): string {
  return formatWhen(iso, { hour: "numeric", minute: "2-digit" });
}

export function formatDay(iso: string): string {
  return formatWhen(iso, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function partsOf(utcMs: number, timeZone: string): Record<string, string> {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(new Date(utcMs)).map((part) => [part.type, part.value]));
  if (parts.hour === "24") parts.hour = "00";
  return parts;
}

function offsetAt(timeZone: string, utcMs: number): number {
  const parts = partsOf(utcMs, timeZone);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - utcMs;
}

export function aucklandLocalToUtc(local: string, timeZone = ZONE): string {
  const match = local.trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) throw new Error("Use a local date like 2026-10-26T10:00");
  const [, year, month, day, hour, minute, second] = match;
  const guess = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second ?? 0));
  const first = guess - offsetAt(timeZone, guess);
  const utc = guess - offsetAt(timeZone, first);
  return new Date(utc).toISOString();
}

export function utcToAucklandInput(iso: string, timeZone = ZONE): string {
  const parts = partsOf(new Date(iso).getTime(), timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function addSeconds(iso: string, seconds: number): string {
  return new Date(new Date(iso).getTime() + seconds * 1000).toISOString();
}

export function durationLabel(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remain = safe % 60;
  if (remain === 0) return `${minutes}m`;
  return `${minutes}m ${remain}s`;
}
